<template>
  <div>
    <b-card no-body class="mb-0 table-no-top table-text-nowrap">
      <b-table
        ref="refProfileListTable"
        class="position-relative mb-25"
        :items="fetchStudentSections"
        responsive
        :fields="sectionFields"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No sections assigned to this user"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Course -->
        <template #cell(name)="data">
          <div class="d-flex align-items-center" style="gap: 0.625rem" v-if="data.item.course">
            <b-avatar
              size="36"
              :src="data.item.course.avatar"
              :text="avatarText(data.item.course.name)"
              :variant="`light-info`"
              :to="{ name: 'apps-roster-course-view', params: { id: data.item.course.id } }"
              style="margin-top: 2px"
              rounded
            />
            <div class="d-flex flex-column" style="gap: 0">
              <div class="d-flex align-items-center font-weight-bold text-nowrap text-white mb-0">
                <div
                  class="cursor-pointer"
                  @click="$router.push({ name: 'apps-roster-course-section-view', params: { id: data.item.id } })"
                >
                  {{ resolveCourseSectionName(data.item) }}
                </div>
                <div v-if="data.item.profile && data.item.profile.reassignFrom" class="ml-50">
                  <feather-icon
                    @click="redirectToReassigned(data.item.profile.reassignFrom, false)"
                    icon="InfoIcon"
                    class="text-warning cursor-pointer"
                    :id="`sr-${data.item.id}-reassign-${data.item.profile.reassignFrom.id}-tooltip`"
                    style="margin-bottom: 2px; z-index: 9"
                  />
                  <b-tooltip
                    placement="right"
                    @click="redirectToReassigned(data.item.profile.reassignFrom, false)"
                    :title="`Reassigned From ${data.item.profile.reassignFrom.name}`"
                    class="cursor-pointer"
                    :target="`sr-${data.item.id}-reassign-${data.item.profile.reassignFrom.id}-tooltip`"
                  />
                </div>
                <div v-if="sectionField(data.item.profile, data.item.id, 'replacedBy')" class="ml-50">
                  <feather-icon
                    @click="redirectToReplaced(sectionField(data.item.profile, data.item.id, 'replacedBy'))"
                    icon="InfoIcon"
                    class="text-warning cursor-pointer"
                    :id="`sr-${data.item.id}-replaced-tooltip`"
                    style="margin-bottom: 2px; z-index: 9"
                  />
                  <b-tooltip
                    placement="right"
                    @click="redirectToReplaced(sectionField(data.item.profile, data.item.id, 'replacedBy'))"
                    :title="`Replaced by another section`"
                    class="cursor-pointer"
                    :target="`sr-${data.item.id}-replaced-tooltip`"
                  />
                </div>
              </div>
              <b-link
                :to="{ name: 'apps-roster-course-section-view', params: { id: data.item.id } }"
                class="d-flex align-items-center text-muted text-nowrap"
              >
                <lucide-icon name="Book" style="width: 14px; height: 14px; margin-right: 1px" />
                <small> {{ data.item.course.name }}</small>
              </b-link>
            </div>
          </div>
        </template>

        <!-- Column: Attributes - Program -->
        <template #cell(fundingSource)="data">
          <b-badge :variant="`light-${resolveProfileProgramVariant(data.item.profile.attributes.fundingSource)}`" class="text-capitalize">
            {{ title(data.item.profile.attributes.fundingSource) }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            :variant="`${resolveProfileSectionStatusVariant(sectionField(data.item.profile, data.item.id, 'status'))}`"
            class="text-capitalize"
          >
            {{ title(sectionField(data.item.profile, data.item.id, 'status')) }}
          </b-badge>
        </template>

        <!-- Column: Transfer of Credit -->
        <template #cell(toc)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(data.item.profile.toc ? data.item.profile.toc.enabled : 'no')}`"
            class="text-capitalize"
          >
            {{ title(data.item.profile.toc && data.item.profile.toc.enabled ? 'Yes' : 'No') }}
          </b-badge>
        </template>

        <!-- Column: Course Exam Voucher -->
        <template #cell(examVoucher)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(sectionField(data.item.profile, data.item.id, 'examVoucher'))}`"
            class="text-capitalize"
          >
            {{ title(sectionField(data.item.profile, data.item.id, 'examVoucher')) }}
          </b-badge>
        </template>

        <!-- Column: Course Exam Taken? -->
        <template #cell(examTaken)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(sectionField(data.item.profile, data.item.id, 'examTaken'))}`"
            class="text-capitalize"
          >
            {{ title(sectionField(data.item.profile, data.item.id, 'examTaken')) }}
          </b-badge>
        </template>

        <!-- Column: Course Exam Passed? -->
        <template #cell(examPassed)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(sectionField(data.item.profile, data.item.id, 'examPassed'))}`"
            class="text-capitalize"
          >
            {{ title(sectionField(data.item.profile, data.item.id, 'examPassed')) }}
          </b-badge>
        </template>

        <!-- Column: Course Exam Passed? -->
        <template #cell(labs)="data">
          <b-badge
            :variant="`light-${resolveProfileYNVariant(sectionField(data.item.profile, data.item.id, 'labCompleted'))}`"
            class="text-capitalize"
          >
            {{ title(sectionField(data.item.profile, data.item.id, 'labCompleted')) }}
          </b-badge>
        </template>

        <!-- Column: Attributes - Payment Status -->
        <!-- <template #cell(paymentStatus)="data">
  <b-badge :variant="`${resolveProfilePaymentStatusVariant(data.item.profile.attributes.paymentStatus)}`" class="text-capitalize">
    {{ title(data.item.profile.attributes.paymentStatus) }}
  </b-badge>
</template> -->

        <!-- Column: Attributes - Uploaded Resume -->
        <template #cell(uploadedResume)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-resume-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'resume')"
            />

            <b-tooltip title="Upload Resume" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-resume-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedResume)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedResume) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded COE -->
        <template #cell(uploadedCoe)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-coe-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'enrollment', 'coe')"
            />

            <b-tooltip title="Upload COE" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-coe-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedCoe)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedCoe) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - Uploaded Transcript -->
        <template #cell(uploadedTranscript)="data">
          <div class="d-flex align-items-center">
            <feather-icon
              :id="`profile-row-${data.item.id}-upload-transcript-icon`"
              icon="UploadIcon"
              class="cursor-pointer mr-25"
              size="16"
              @click="startUpload(data.item, 'transcript', 'prior')"
            />

            <b-tooltip title="Upload Transcript" class="cursor-pointer" :target="`profile-row-${data.item.id}-upload-transcript-icon`" />

            <b-badge :variant="`light-${resolveProfileYNVariant(data.item.profile.attributes.uploadedTranscript)}`" class="text-capitalize">
              {{ title(data.item.profile.attributes.uploadedTranscript) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Attributes - VA Submission -->
        <template #cell(submittedToVa)="data">
          <profile-submitted-to-va-select :profile="data.item.profile" />
        </template>

        <!-- Column: Total Students -->
        <template #cell(sessionDate)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">
              {{ moment(data.item.sessionDate).format('L') }}
            </span>
          </div>
        </template>

        <!-- Column: Retake -->
        <template #cell(retake)="data">
          <b-badge :variant="`${resolveProfileYNVariant(data.item.profile.attributes.retake || false)}`" class="text-capitalize">
            {{ title(data.item.profile.attributes.retake || 'No') }}
          </b-badge>
        </template>

        <!-- Column: Compliance Score -->
        <template #cell(compliance)="data">
          <template
            v-if="
              !data.item.profile.compliance ||
              !data.item.profile.compliance.sections ||
              !data.item.profile.compliance.sections[data.item.id]
            "
          >
            <b-badge variant="danger">N/A</b-badge>
          </template>
          <template v-else>
            <b-badge
              :variant="`${resolveComplianceScoreVariant({
                score: data.item.profile.compliance.sections[data.item.id].scores.overall,
              })}`"
              :id="`compliance-${data.item.id}`"
              class="text-capitalize"
              style="cursor: help"
            >
              {{
                gradeScore({
                  score: data.item.profile.compliance.sections[data.item.id].scores.overall,
                  percent: true,
                  status: data.item.profile.sections[data.item.id].status,
                })
              }}
              <font-awesome-icon v-if="data.item.profile.compliance.sections[data.item.id].locked" icon="fa-regular fa-lock" />
            </b-badge>

            <compliance-popover
              :target-id="data.item.id"
              :student-name="data.item.student.firstName"
              :compliance-data="data.item.profile.compliance.sections[data.item.id]"
              :profile-data="data.item.profile"
            />
          </template>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button" :popper-opts="{ positionFixed: true }">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item
              v-if="$can('roster.profile.update')"
              @click="
                $router.push({ name: 'apps-roster-profile-edit', params: { id: data.item.profile.id }, query: { section: data.item.id } })
              "
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-divider v-if="false" />

            <b-dropdown-item v-if="false" @click="openSectionReplaceModal(data.item, true)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Replace</span>
                <feather-icon icon="MoveIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-item v-if="false && $can('roster.profile.delete')" @click="archiveProfile(data.item.id)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Archive</span>
                <feather-icon icon="ArchiveIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-75 mt-0">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="mb-0 text-muted">{{ totalSections }} Total Entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
            <div class="mr-2 vue-select-up">
              <span class="mb-0 mr-75">Rows Per Page</span>
              <v-select
                size="s"
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
                :open="true"
              />
            </div>
            <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSections"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-sm-0 ml-lg-50"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import ProfileSubmittedToVaSelect from '@/views/apps/roster/profile/components/ProfileSubmittedToVaSelect.vue';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BTooltip,
} from 'bootstrap-vue';
import { avatarText, gradeScore, title } from '@core/utils/filter';
import usePersistedPerPage from '@/libs/utils/perPage';
import store from '@/store';
import { ref } from '@vue/composition-api';
import moment from 'moment';
import useCourseSections from '@/views/apps/roster/courses/sections/useCourseSections';
import { momentTzFormatted } from '@/libs/utils/time';
import useProfiles from '@/views/apps/roster/profile/useProfiles';
import VueContext from 'vue-context';
import vSelect from 'vue-select';
import CompliancePopover from '@/views/apps/roster/courses/sections/view/components/CompliancePopover.vue';

export default {
  components: {
    ProfileSubmittedToVaSelect,
    CompliancePopover,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BTooltip,
    VueContext,
    vSelect,
  },
  props: {
    studentId: {
      type: String,
      required: true,
    },
  },

  methods: {
    redirectToReplaced(section) {
      return this.$router.push({ name: 'apps-roster-course-section-view', params: { id: section } });
    },
    redirectToReassigned(cohort) {
      if (cohort.type === 'course' && Array.isArray(cohort.sections) && cohort.sections.length > 0) {
        const id = cohort.sections[0].id ? cohort.sections[0].id : cohort.sections[0];
        return this.$router.push(`/roster/courses/sections/${id}`);
      }
      if (cohort.type === 'pathway') {
        const id = cohort.id ? cohort.id : cohort._id;
        return this.$router.push(`/roster/pathways/cohorts/${id}`);
      }
    },
    sectionField(profile, section, field) {
      const sectionId = section;

      if (!profile.sections || !profile.sections[sectionId]) {
        return 'na';
      }

      return profile.sections[sectionId][field];
    },
  },
  setup(props) {
    const sectionFields = ref([
      { key: 'name', sortable: true },
      { key: 'status', sortable: true },
      { key: 'fundingSource', label: 'Funding', sortable: true },
      { key: 'compliance', label: 'Compliance', sortable: false },
      { key: 'retake', label: 'Retake', sortable: false },
      { key: 'toc', label: 'ToC', sortable: true },
      { key: 'examVoucher', sortable: true },
      { key: 'examTaken', sortable: true },
      { key: 'examPassed', sortable: true },
      { key: 'labs', sortable: true },
      { key: 'actions' },
    ]);
    const sectionsData = ref({});
    const currentPage = ref(1);
    const perPage = usePersistedPerPage('studentRelationsSectionList', 10);
    const totalSections = ref(0);
    const totalPageCount = ref(1);

    const isSortDirDesc = ref(false);
    const sortBy = ref('startDate');

    const fetchStudentSections = (ctx) => {
      const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
      const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

      const promise = store.dispatch('studentStoreModule/fetchStudentSections', {
        id: props.studentId,
        queryParams: {
          limit: perPage.value,
          page: currentPage.value,
          populate: 'instructor course profile.compliance',
          sortBy: sb,
        },
      });

      return promise
        .then((response) => {
          const { results, totalResults, totalPages } = response.data;

          sectionsData.value = results;
          totalSections.value = totalResults;
          totalPageCount.value = totalPages;
          return results;
        })
        .catch((error) => {
          console.error(error);
          if (error.response.status === 404) {
            sectionsData.value = undefined;
          }
        });
    };

    const resolveCourseStatusVariant = (status) => {
      if (status === 'created') return 'secondary';
      if (status === 'enrolling') return 'info';
      if (status === 'waiting') return 'warning';
      if (status === 'live') return 'info';
      if (status === 'completed') return 'success';
      return 'info';
    };

    const resolveCourseSectionStartDate = (courseSection) => {
      return momentTzFormatted(courseSection.startDate, 'MMMM Do YYYY');
    };

    const {
      resolveUserRoleVariant,
      resolveCourseSectionName,
      resolveCourseSectionStatusVariant,
      resolveCourseSectionStatusIcon,
      resolveCourseSectionVisibilityVariant,
      resolveCourseSectionVisibilityIcon,
      resolveCourseSectionDateMessage,
      resolveCourseSectionDateMessageIcon,
      resolveCourseSectionDateMessageVariant,
    } = useCourseSections();

    const {
      perPageOptions,

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,

      resolveComplianceScoreVariant,
    } = useProfiles();

    return {
      fetchStudentSections,
      sectionFields,
      sectionsData,
      avatarText,
      title,
      moment,
      gradeScore,
      resolveCourseStatusVariant,

      resolveUserRoleVariant,
      resolveCourseSectionName,
      resolveCourseSectionStatusVariant,
      resolveCourseSectionStatusIcon,
      resolveCourseSectionVisibilityIcon,
      resolveCourseSectionVisibilityVariant,
      resolveCourseSectionDateMessage,
      resolveCourseSectionDateMessageIcon,
      resolveCourseSectionDateMessageVariant,
      resolveCourseSectionStartDate,

      perPageOptions,

      resolveProfileVisibilityVariant,
      resolveProfileStatusVariant,
      resolveProfileSectionStatusVariant,
      resolveProfilePaymentStatusVariant,
      resolveProfileProgramVariant,
      resolveProfileYNVariant,
      resolveComplianceScoreVariant,

      sortBy,
      isSortDirDesc,
      currentPage,
      perPage,
      totalSections,
      totalPageCount,
    };
  },
};
</script>
